import React, { useState, useEffect } from 'react';
import { imageManifest } from '../../utils/image-manifest';

interface ResponsiveHeroImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  onLoad?: () => void;
  // Mobile-specific positioning
  mobilePosition?: string;
  desktopPosition?: string;
  // Art direction - different crops for mobile
  mobileFocalPoint?: 'top' | 'center' | 'bottom' | 'custom';
  customMobilePosition?: string;
}

export default function ResponsiveHeroImage({
  src,
  alt,
  className = '',
  loading = 'lazy',
  priority = false,
  onLoad,
  mobilePosition = 'center center',
  desktopPosition = 'center center',
  mobileFocalPoint = 'center',
  customMobilePosition
}: ResponsiveHeroImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showBlur, setShowBlur] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Extract filename from path
  const filename = src.split('/').pop() || '';
  const imageData = imageManifest.images[filename as keyof typeof imageManifest.images];

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determine focal point for mobile
  const getMobilePosition = () => {
    switch (mobileFocalPoint) {
      case 'top':
        return 'center top';
      case 'bottom':
        return 'center bottom';
      case 'center':
        return 'center center';
      case 'custom':
        return customMobilePosition || mobilePosition || 'center center';
      default:
        return mobilePosition || 'center center';
    }
  };

  // Fallback to original if not in manifest
  if (!imageData) {
    return (
      <div className={`relative overflow-hidden ${className}`}>
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          style={{
            objectPosition: isMobile ? getMobilePosition() : desktopPosition
          }}
          loading={loading}
          onLoad={() => {
            setIsLoaded(true);
            onLoad?.();
          }}
        />
      </div>
    );
  }

  // Preload if priority
  useEffect(() => {
    if (priority && imageData) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.type = 'image/webp';
      link.imageSrcset = imageData.srcsets.webp;
      link.imageSizes = '100vw';
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(link);
      };
    }
  }, [priority, imageData]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Blur placeholder with gradient for better mobile appearance */}
      {showBlur && imageData.blurDataURL && (
        <div className="absolute inset-0">
          <img
            src={imageData.blurDataURL}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full object-cover scale-110 blur-xl transition-opacity duration-700 ${
              isLoaded ? 'opacity-0' : 'opacity-100'
            }`}
            style={{
              objectPosition: isMobile ? getMobilePosition() : desktopPosition
            }}
          />
          {/* Mobile gradient overlay for better text contrast */}
          {isMobile && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 md:hidden" />
          )}
        </div>
      )}

      {/* Main image with picture element for art direction */}
      <picture
        className={`transition-opacity duration-700 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Mobile-optimized source - use smaller sizes */}
        <source
          media="(max-width: 767px)"
          type="image/webp"
          srcSet={imageData.srcsets.webp}
          sizes="100vw"
        />
        
        {/* Desktop WebP */}
        <source
          media="(min-width: 768px)"
          type="image/webp"
          srcSet={imageData.srcsets.webp}
          sizes="100vw"
        />
        
        {/* Mobile JPEG fallback */}
        <source
          media="(max-width: 767px)"
          type="image/jpeg"
          srcSet={imageData.srcsets.jpg}
          sizes="100vw"
        />
        
        {/* Desktop JPEG fallback */}
        <source
          media="(min-width: 768px)"
          type="image/jpeg"
          srcSet={imageData.srcsets.jpg}
          sizes="100vw"
        />
        
        {/* Fallback img tag with responsive positioning */}
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          style={{
            objectPosition: isMobile ? getMobilePosition() : desktopPosition,
            // Subtle zoom on mobile for better composition (reduced from 1.1)
            transform: isMobile ? 'scale(1.05)' : 'scale(1)'
          }}
          loading={loading}
          onLoad={() => {
            setIsLoaded(true);
            setShowBlur(false);
            onLoad?.();
          }}
        />
      </picture>

      {/* Enhanced mobile overlay for better readability */}
      {isMobile && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent md:hidden pointer-events-none" />
      )}
    </div>
  );
}